import { Box, Button, ButtonGroup, CircularProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { produce } from "immer";
import { useCallback, useEffect, useRef, useState } from "react";
import { FrameToTimecode, SecToFrame, SecToTimecode } from "../../Utils/Timecode/ReactPlayer";
import Peaks from "peaks.js";
import { cacheService, jobBrokerService, mediaService } from "../../Core/Api/services";
import axios from "axios";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import _ from "underscore";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import { v4 as uuidv4 } from "uuid";
import { Caption, UpdateCaptionsEventProps } from "./scribetool";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import PanToolIcon from "@mui/icons-material/PanTool";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { GetRulesMatchingObjectRequest } from "../../Core/Api/Types/get-rules-request";
import { JobRuleTag } from "../../Core/Api/Types/job-rule-tag";
import { useTranslation } from "react-i18next";
export interface PeaksWrapperProps {
  captions: Caption[];
  setCaptions: React.Dispatch<React.SetStateAction<any[]>>;
  setDragSegmentId: React.Dispatch<React.SetStateAction<string | null>>;
  triggerUpdate: boolean;
  setTriggerUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  waveformFile: any;
  assetId: number | undefined;
  duration: any;
  setDuration: any;
  videoFps: number;
  captionsRef: any;
  videoRef: any;
  showTimeline: boolean;
  currentTextTrack: React.MutableRefObject<any>;
  updateCaptionEvent: UpdateCaptionsEventProps | null;
  mode: "unknown" | "stp" | "sub" | "project";
}

function str2ab(str) {
  var buf = new ArrayBuffer(str.length * 2); // 2 bytes for each char
  var bufView = new Uint8Array(buf);
  for (var i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

export const PeaksWrapper: React.FC<PeaksWrapperProps> = (props) => {
  const {
    captions,
    duration,
    assetId,
    setCaptions,
    setDragSegmentId,
    setDuration,
    videoFps,
    waveformFile,
    captionsRef,
    videoRef,
    triggerUpdate,
    setTriggerUpdate,
    showTimeline,
    currentTextTrack,
    updateCaptionEvent,
    mode,
  } = props;
  const { t } = useTranslation();
  const peakRef = useRef<any>(undefined);
  const peakContainerRef = useRef<HTMLDivElement>(undefined);
  const peakZoomRef = useRef<HTMLDivElement>(undefined);
  const scrollContainerRef = useRef<any>(undefined);
  const resizeObserver = new ResizeObserver(_.debounce(onResize, 500));
  const theme = useTheme();
  const customSnackbar = useCustomSnackbar();
  const [peakLoading, setPeakLoading] = useState<boolean>(false);
  const [layout, setLayout] = useState<"both" | "zoom-only" | "minimize">("both");
  const [followMedia, setFollowMedia] = useState<boolean>(true);
  const [dragMode, setDragMode] = useState<"scroll" | "insert-segment">("scroll");
  const [waveformJobRuleId, setWaveformJobRuleId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  function getCurrentVideoDurationTimecode() {
    if (!peakRef.current || !peakRef.current.player) return null;

    const secs = peakRef.current.player.getDuration();
    return FrameToTimecode(SecToFrame(secs, videoFps), videoFps);
  }

  function peaksInitPostOperations(peaks) {
    setDuration(getCurrentVideoDurationTimecode());

    peaks.on("player.seeked", function (time) {});

    peaks.on("segments.dragstart", function (data) {
      setDragSegmentId(data.segment._id);
      setTriggerUpdate((x) => !x);
    });

    peaks.on("segments.dragend", function (data, inMarker) {
      setCaptions(
        produce((draft) => {
          var caption = draft.find((c) => c.segmentId === data.segment._id);

          if (!caption) return;
          caption.tcIn = FrameToTimecode(SecToFrame(data.segment.startTime, videoFps), videoFps);
          caption.tcOut = FrameToTimecode(SecToFrame(data.segment.endTime, videoFps), videoFps);

          caption.cue.startTime = data.segment.startTime;
          caption.cue.endTime = data.segment.endTime;
          caption.secIn = data.segment.startTime;
          caption.secOut = data.segment.endTime;

          draft = draft.sort((a, b) => a.secIn - b.secIn);
          captionsRef.current = draft;
        })
      );
      setTriggerUpdate((x) => !x);
    });

    peaks.on("segments.add", handleAddSegment);

    const view = peakRef.current?.views.getView("zoomview");
    if (view) {
      view.setSegmentDragMode("no-overlap");
    }
    setPeakLoading(false);
  }

  const handleAddSegment = useCallback(
    (event) => {
      let segment: any = null;
      if (event.insert) {
        segment = event.segments[0];
      }
      if (!segment) return;

      var segmentId = uuidv4();
      segment.update({ id: segmentId, color: "#00e180" });

      var cue = new VTTCue(segment.startTime, segment.endTime, "");
      var newCaption = {
        cue: cue,
        style: "Normal",
        tcIn: SecToTimecode(segment.startTime, videoFps),
        tcOut: SecToTimecode(segment.endTime, videoFps),
        secIn: segment.startTime,
        secOut: segment.endTime,
        note: "",
        status: "",
        segmentId: segmentId,
      };
      const newCaptions = [...(captionsRef.current ?? []), newCaption].sort((a, b) => a.secIn - b.secIn);
      setCaptions(newCaptions);
      captionsRef.current = newCaptions;
      currentTextTrack.current && currentTextTrack.current.addCue(cue);
      setTriggerUpdate((x) => !x);
    },
    [triggerUpdate, captions, captions.length]
  );

  function onResize(entries) {
    for (const entry of entries) {
      if (entry.target === peakZoomRef) {
        const view = peakRef.current.views.getView("zoomview");

        if (view) {
          view.fitToContainer();
        }
      } else if (entry.target === scrollContainerRef.current) {
        const scrollbar = peakRef.current.views.getScrollbar();

        if (scrollbar) {
          scrollbar.fitToContainer();
        }
      } else if (entry.target === peakContainerRef) {
        const view = peakRef.current.views.getView("overview");

        if (view) {
          view.fitToContainer();
        }
      }
    }
  }

  const handleZoomIn = () => {
    peakRef.current?.zoom.zoomIn();
  };

  const handleZoomOut = () => {
    peakRef.current?.zoom.zoomOut();
  };

  const handleLayout = (e) => {
    layout === "both" ? setLayout("zoom-only") : setLayout("both");
  };

  const handleSwitchDragMode = () => {
    if (dragMode === "insert-segment") setDragMode("scroll");
    else setDragMode("insert-segment");
  };

  const handleToggleFollowMedia = () => {
    const zoomView = peakRef.current?.views.getView("zoomview");
    zoomView.enableAutoScroll(!followMedia, { offset: 100 });
    setFollowMedia((x) => !x);
  };
  useEffect(() => {
    if (!waveformFile || !currentTextTrack.current || !videoRef.current || !assetId) return;
    if (peakRef.current) {
      peakRef.current.destroy();
      setDuration(null);
    }
    setPeakLoading(true);
    axios.get(mediaService.DownloadMediaFile(assetId, waveformFile.id, cacheService.GetAuthKey() || "")).then((res) => {
      if (res.status === 200) {
        Peaks.init(
          {
            zoomLevels: [512, 1024, 2048, 4096, 8192, 16384, 32768],
            zoomview: {
              container: peakZoomRef.current,
              playheadTextColor: theme.palette.mode === "dark" ? "white" : "black",
              axisLabelColor: theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[800],
              waveformColor: theme.palette.info.main,
              playheadColor: theme.palette.primary.mainHover,
              segmentOptions: {
                startMarkerColor: theme.palette.mode === "dark" ? theme.palette.grey[600] : theme.palette.grey[700],
                endMarkerColor: theme.palette.mode === "dark" ? theme.palette.grey[600] : theme.palette.grey[700],
              },
            },
            overview: {
              container: peakContainerRef.current,
              playheadTextColor: theme.palette.mode === "dark" ? "white" : "black",
              axisLabelColor: theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[800],
              playheadColor: theme.palette.primary.mainHover,
            },
            scrollbar: {
              container: scrollContainerRef.current,
              color: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[300],
            },
            mediaElement: videoRef.current,
            waveformData: { arraybuffer: str2ab(res.data) },
          },
          (err, peaks) => {
            if (err || !peaks) {
              console.log("errr", err);
              return;
            }
            captions.forEach((x) => {
              convertCaptionToSegment(x, peaks);
            });
            captionsRef.current = captions;
            peakRef.current = peaks;
            peaksInitPostOperations(peaks);
          }
        );
      } else {
      }
    });
  }, [waveformFile, currentTextTrack.current, videoRef.current, assetId]);

  useEffect(() => {
    if (!waveformFile && assetId) {
      let request: GetRulesMatchingObjectRequest = {
        objectIds: [assetId],
      };
      jobBrokerService.ListRulesByObjects(request).then((response) => {
        const waveformJobRule = response.rules?.find((x) => x.tag === JobRuleTag.WaveformGenerate);
        if (waveformJobRule) setWaveformJobRuleId(waveformJobRule.id);
      });
    }
  }, [waveformFile, assetId]);

  const convertCaptionToSegment = (caption: Caption, peaks) => {
    peaks.segments.add({
      id: caption.segmentId,
      startTime: caption.secIn,
      endTime: caption.secOut,
      labelText: "",
      editable: true,
      color: "#00e180",
    });
  };

  const handleGenerateWaveform = () => {
    jobBrokerService.ApplyRuleOnObjects({ objectIds: [assetId!], ruleId: waveformJobRuleId }).then((response) => {
      if (response.success) {
        customSnackbar(t("alert:success.jobRuleApplied_one"), "success");
        setIsGenerating(true);
      } else customSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  };

  useEffect(() => {
    if (layout === "both") {
      setTimeout(() => {
        peakRef.current?.views.createOverview(peakContainerRef.current);
      }, 0);
    } else {
      peakRef.current?.views.destroyOverview();
    }

    setTimeout(() => {
      const zoomview = peakRef.current?.views.getView("zoomview");
      if (zoomview) {
        zoomview.fitToContainer();
      }
    }, 50);

    setTimeout(() => {
      const view = peakRef.current?.views.getView("overview");
      if (view) {
        view.fitToContainer();
      }
    }, 50);
    setTimeout(() => {
      const scrollbar = peakRef.current?.views.getScrollbar();

      if (scrollbar) {
        scrollbar.fitToContainer();
      }
    }, 50);
  }, [layout]);

  useEffect(() => {
    if (!waveformFile) return;
    peakZoomRef.current && resizeObserver.observe(peakZoomRef.current);
    peakContainerRef.current && resizeObserver.observe(peakContainerRef.current);
  }, [waveformFile, peakZoomRef.current, peakContainerRef.current]);

  useEffect(() => {
    if (updateCaptionEvent !== null && peakRef.current) {
      switch (updateCaptionEvent.type) {
        case "create":
          convertCaptionToSegment(updateCaptionEvent.data, peakRef.current);
          break;
        case "update":
          break;
        case "delete":
          peakRef.current.segments.removeById(updateCaptionEvent.segmentId);
          break;
        default:
          break;
      }
    }
  }, [updateCaptionEvent]);

  useEffect(() => {
    const view = peakRef.current?.views.getView("zoomview");
    if (view) {
      view.setWaveformDragMode(dragMode);
    }
  }, [dragMode]);

  if (!waveformFile || !currentTextTrack.current || !videoRef.current)
    return (
      <Stack
        height={showTimeline ? "212px" : "1px"}
        width={"100%"}
        display={"flex"}
        direction={"row"}
        justifyContent={"center"}
        alignContent={"center"}
        alignItems={"center"}
        overflow={"hidden"}
        border={theme.palette.mode === "dark" ? "0px solid default" : `1px solid ${theme.palette.grey[300]}`}
        sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
      >
        {!waveformFile ? (
          <Box
            p={1}
            sx={{
              height: "90px",
              borderRadius: "12px",
            }}
            width={"200px"}
            display={"flex"}
            justifyContent={"space-between"}
            alignContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
          >
            <Typography variant="h6">{t("page:body.noWaveformData")}</Typography>
            <Button
              disabled={waveformJobRuleId === null || isGenerating}
              startIcon={isGenerating ? <CircularProgress size={"18px"} /> : <GraphicEqIcon />}
              onClick={handleGenerateWaveform}
              variant="contained"
              color="primary"
            >
              {t("button:generateWaveform")}
            </Button>
          </Box>
        ) : (
          <Box
            p={1}
            sx={{
              height: "90px",
              borderRadius: "12px",
            }}
            width={"200px"}
            display={"flex"}
            justifyContent={"space-between"}
            alignContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
          >
            <Typography variant="h6">{t("page:body.waitingMediaToLoad")}</Typography>
          </Box>
        )}
      </Stack>
    );

  return (
    <Stack
      height={showTimeline ? "212px" : "1px"}
      position={"relative"}
      width={layout === "minimize" ? "50%" : "100%"}
      display={"flex"}
      direction={"row"}
      top={"8px"}
      spacing={2}
      borderTop={theme.palette.mode === "dark" ? "0px solid default" : `1px solid ${theme.palette.grey[300]}`}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        marginTop: "0px !important",
        cursor: dragMode === "insert-segment" ? "crosshair" : "default",
      })}
    >
      <Box width={layout === "both" ? "550px" : "1px"}>
        <Box
          sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
          width={"100%"}
          height={"9px"}
        ></Box>
        <Box
          sx={(theme) => ({ backgroundColor: theme.palette.background.paper })}
          height={showTimeline ? "194px" : "1px"}
          ref={peakContainerRef}
          width={"100%"}
        ></Box>
        <Box
          sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
          width={"100%"}
          height={"9px"}
        ></Box>
      </Box>
      <Stack
        sx={(theme) => ({
          opacity: peakLoading ? 0 : 1,
          backgroundColor: theme.palette.background.default,
          marginLeft: "0px !important",
        })}
        spacing={1}
        justifyContent={"space-between"}
        display={"flex"}
        flexDirection={"column"}
        pt={2}
        pb={2}
        pl={1}
        pr={1}
        height={showTimeline ? "212px" : "1px"}
        overflow={"hidden"}
      >
        <ButtonGroup orientation="vertical">
          <Tooltip title={layout === "both" ? t("tooltip:title.hideOverview") : t("tooltip:title.showOverview")}>
            <Button sx={{ height: "32px" }} onClick={handleLayout}>
              {layout === "both" ? <ZoomOutMapIcon /> : <VerticalSplitIcon />}
            </Button>
          </Tooltip>
        </ButtonGroup>
        <ButtonGroup orientation="vertical">
          <Tooltip
            title={
              dragMode === "scroll" ? t("tooltip:title.switchToInsertMode") : t("tooltip:title.switchToScrollMode")
            }
          >
            <Button disabled={mode === "stp"} sx={{ height: "32px" }} onClick={handleSwitchDragMode}>
              {dragMode === "scroll" ? <PanToolIcon /> : <PlaylistAddIcon />}
            </Button>
          </Tooltip>
        </ButtonGroup>
        <ButtonGroup orientation="vertical">
          <Tooltip title={followMedia ? t("tooltip:title.disableAutoScroll") : t("tooltip:title.enableAutoScroll")}>
            <Button
              sx={{ height: "32px" }}
              color="info"
              variant={!followMedia ? "contained" : "outlined"}
              onClick={handleToggleFollowMedia}
            >
              <PlayArrowIcon />
            </Button>
          </Tooltip>
        </ButtonGroup>
        <ButtonGroup orientation="vertical">
          <Tooltip title={t("tooltip:title.zoomIn")}>
            <Button sx={{ height: "32px" }} variant="outlined" onClick={handleZoomIn}>
              <ZoomInIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t("tooltip:title.zoomOut")}>
            <Button sx={{ height: "32px" }} variant="outlined" onClick={handleZoomOut}>
              <ZoomOutIcon />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Stack>
      <Box
        sx={(theme) => ({ backgroundColor: theme.palette.background.paper })}
        height={showTimeline ? "212px" : "1px"}
        width={layout === "both" ? "calc(100% - 611px)" : "100%"}
        marginLeft={"0px !important"}
        marginRight={"0px !important"}
      >
        <Box
          sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
          width={"100%"}
          height={"9px"}
        ></Box>
        <Box width={"100%"} ref={peakZoomRef} height={"194px"}></Box>
        <Box
          sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
          ref={scrollContainerRef}
          width={"100%"}
          height={"9px"}
        ></Box>
      </Box>
      {peakLoading && (
        <CircularProgress
          sx={{
            position: "absolute",
            top: "45%",
            left: "48.3%",
          }}
        />
      )}
    </Stack>
  );
};
